import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, Send, MapPin } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import CopyButton from "@/components/admin/CopyButton";

const API = getApiUrl();

/**
 * Admin -> Integrations -> Ideal Postcodes
 * UK postcode -> address lookup (getAddress.io / Ideal Postcodes), used by
 * the public Capture Property form's postcode field.
 */
export default function IdealPostcodesTestingSection() {
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKeyMasked, setApiKeyMasked] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPostcode, setTestPostcode] = useState("EC1A 1BB");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/integrations/ideal-postcodes-settings`, { withCredentials: true });
      setBaseUrl(data.base_url || "");
      setApiKeyMasked(data.api_key_masked || "");
      setHasKey(!!data.has_key);
    } catch (e) {
      console.error("Failed to load Ideal Postcodes settings:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/integrations/ideal-postcodes-settings`,
        { api_key: apiKeyInput || undefined, base_url: baseUrl || undefined },
        { withCredentials: true }
      );
      setApiKeyInput("");
      toast.success("Ideal Postcodes settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/ideal-postcodes-test-call`,
        { postcode: testPostcode },
        { withCredentials: true }
      );
      setResult(data);
      if (data.status === "sent" && data.http_status < 400) {
        toast.success(`Lookup succeeded (HTTP ${data.http_status})`);
      } else {
        toast.error("Lookup failed — see response below");
      }
    } catch (e) {
      setResult({ status: "error", detail: e?.response?.data?.detail || e.message });
      toast.error("Test call failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ideal-postcodes-testing-section">
      <IntegrationApiLogToggle provider="ideal_postcodes" />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> Ideal Postcodes Settings
          </CardTitle>
          <CardDescription>
            UK postcode → address lookup, used by the public Capture Property form's postcode field.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="ip-base-url">Base URL</Label>
                <Input
                  id="ip-base-url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://ga.ideal-postcodes.co.uk"
                  data-testid="ideal-postcodes-base-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ip-api-key">API Key {hasKey && <span className="text-xs text-muted-foreground">(currently {apiKeyMasked})</span>}</Label>
                <Input
                  id="ip-api-key"
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder={hasKey ? "Leave blank to keep current key" : "Enter API key"}
                  data-testid="ideal-postcodes-api-key-input"
                />
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="ideal-postcodes-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" /> Test Lookup
          </CardTitle>
          <CardDescription>Run a real autocomplete call against a UK postcode to confirm the integration works.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="ip-test-postcode">Postcode</Label>
              <Input
                id="ip-test-postcode"
                value={testPostcode}
                onChange={(e) => setTestPostcode(e.target.value)}
                placeholder="EC1A 1BB"
                data-testid="ideal-postcodes-test-postcode-input"
              />
            </div>
            <Button onClick={handleTest} disabled={testing} data-testid="ideal-postcodes-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Test
            </Button>
          </div>
          {result && (
            <div>
              <div className="flex items-center justify-end mb-1">
                <CopyButton text={JSON.stringify(result, null, 2)} testId="ideal-postcodes-result-copy" />
              </div>
              <pre
                className="text-xs bg-muted/40 border rounded p-3 max-h-64 overflow-y-auto whitespace-pre-wrap break-words"
                data-testid="ideal-postcodes-test-result"
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
